import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ensureFreshGuestToken,
  refreshGuestTokenNow,
  fetchSkpEnvironment,
  getShareLinkFileDownloadUrl,
  getShareLinkFilePreviewUrl,
  getShareLinkFileThumbnailUrl,
  getShareLinkZipUrl,
  resolveShareLink,
} from "../../api";
import { DownloadAllButtonIcon } from "../../components/downloadAllButtonIcon";
import { ItemActivation } from "../../components/fileCard";
import {
  FileGrid,
  FILE_GRID_GAP_PX,
  FILE_GRID_TEMPLATE_COLUMNS,
} from "../../components/fileGrid";
import { FileList } from "../../components/fileList";
import { RollingNumber } from "../../components/rollingNumber";
import PreviewPage, { previewImageRequestSize } from "../preview/Preview";
import {
  ShareLinkFile,
  ShareLinkResolution,
  SkpEnvironment,
} from "../../entities";
import { useHasHover } from "../../hooks/useHasHover";
import { useMarqueeSelection } from "../../hooks/useMarqueeSelection";
import { useFileRangeSelection } from "../../hooks/useFileRangeSelection";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useFileSort } from "../../hooks/useFileSort";
import {
  DRIVE_FAVICON_HREF,
  DRIVE_PRODUCT_NAME,
  usePageChrome,
} from "../../hooks/usePageChrome";
import { useSelectionKeyboard } from "../../hooks/useSelectionKeyboard";
import { handleGridFocusCycle } from "../../utils/focusCycle";
import { findSelectableItem } from "../../utils/selectable";
import { SelectionToolbar } from "../../components/selectionToolbar";
import { ConditionalEnvironmentProvider } from "../../providers/environment";
import { cssVar } from "../../utils/cssVar";
import { formatDateDDMMYYYY, parseApiDate } from "../../utils/date";
import {
  basenameFromPath,
  formatFileSize,
  isInlinePreviewMime,
} from "../../utils/fileFormat";
import { FileSort, FileSortKey, sortFiles } from "../../utils/fileSort";
import ShareLinkErrorPage from "./shareLinkError";
import { ErrorPage } from "../error";
import TransferPageSkeleton from "../skeletons/transfer";

/**
 * Below this viewport width the files render as a list (rows) instead of the
 * card grid. Matches Tailwind's `sm` breakpoint (640px). The grid constants
 * (`FILE_GRID_*`) live in {@link FileGrid}, imported above for the skeleton.
 */
const COMPACT_VIEW_MEDIA_QUERY = "(max-width: 639px)";

type DriveShareLinkPageParams = {
  shareLinkUid: string;
};

/**
 * Renders the public (unauthenticated) viewer for a Drive share-link.
 *
 * The page:
 *  - Resolves the share-link (which issues an HttpOnly guest-token cookie
 *    scoped to `/api/drive/v3/get/{shareLinkUid}`).
 *  - Lists the shared files with icons and sizes.
 *  - Offers per-file download, a "Download all as ZIP" action (when applicable),
 *    and the space avatar if available.
 *  - Transparently re-resolves the share-link shortly before the guest
 *    token's `exp` to keep file downloads working.
 *
 * @see {@link https://dev.skalio.net/drive/api/v3/#share-links Drive API v3 · Share-links}
 */
export default function DriveShareLinkPage() {
  const { shareLinkUid } = useParams<DriveShareLinkPageParams>();
  const { i18n } = useTranslation();

  // The router should prevent this from happening.
  if (!shareLinkUid) {
    throw new Error("shareLinkUid is undefined");
  }

  // Drive tab chrome (a share-link is a Drive feature, not a transfer).
  usePageChrome({
    title: DRIVE_PRODUCT_NAME,
    faviconHref: DRIVE_FAVICON_HREF,
  });

  // Environment is fetched from the current host (same origin) — it is only
  // needed to decorate the top banner, footer and locale switcher. The
  // share-link itself is self-contained and does not depend on it.
  const environmentRef = useRef<SkpEnvironment | undefined>(undefined);

  const { status, data, error } = useQuery<ShareLinkResolution, AxiosError>({
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: ["shareLinkResolution", shareLinkUid],
    queryFn: async () => {
      try {
        environmentRef.current = await fetchSkpEnvironment();
      } catch {
        // Environment fetch is best-effort; the share-link must still render.
      }
      return await resolveShareLink(shareLinkUid);
    },
  });

  // The guest token is refreshed reactively (before downloads, and on a 401
  // from a guest-authenticated request, see refreshGuestTokenNow) rather than
  // on an exp-based timer, which was prone to clock skew, background-tab
  // throttling, and had no failure recovery. As a safety net for long-open
  // tabs, also refresh when the tab becomes visible again (throttled inside
  // ensureFreshGuestToken).
  useEffect(() => {
    if (status !== "success") return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void ensureFreshGuestToken(shareLinkUid);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [status, shareLinkUid]);

  // Wraps the file region; used both to box-select cards (marquee) and to
  // locate the last card for the Tab focus cycle.
  const filesContainerRef = useRef<HTMLDivElement>(null);

  // Which layout the viewport gets. Owned here (not in the files section) because
  // it decides the display order below, which the selection and the preview's
  // paging both follow.
  const isCompact = useMediaQuery(COMPACT_VIEW_MEDIA_QUERY);
  // Column sort: unsorted until a header is clicked, held for this page view
  // only (a share-link is opened once, by a recipient with no preference for us
  // to remember), and applied by the list view only, since its headers are the
  // control for it. Until then, and in the grid, the files keep the order the
  // share-link was created with, which is frequently the point of one.
  const { sort, onSort } = useFileSort();
  const files = useMemo(() => {
    const resolved = data?.files ?? [];
    return sort && isCompact
      ? sortFiles(resolved, sort, i18n.language)
      : resolved;
  }, [data?.files, isCompact, sort, i18n.language]);

  // The universe for "select all"; memoized so the selection hook's `selectAll`
  // stays referentially stable across renders.
  const allUids = useMemo(() => files.map((f) => f.uid), [files]);
  const selection = useFileSelection(allUids);
  // Desktop-file-manager click gestures: a plain click moves the selection,
  // cmd/ctrl+click toggles, shift+click takes the range from the last click.
  const range = useFileRangeSelection({ files, selection });

  // Marquee (drag-select). The mouse-down handler + `select-none` live on the
  // files-area wrapper only (see render), so a drag can only *start* within the
  // files section (and the padding just above the grid) — not the header. Once
  // started it still grows anywhere (the hook listens on `window`).
  // `filesContainerRef` wraps the file region for card hit-testing and the Tab
  // focus cycle.
  const marquee = useMarqueeSelection({
    containerRef: filesContainerRef,
    onSelect: selection.setSelection,
    enabled: files.length > 0,
  });

  // Cmd/Ctrl+A selects every file; Escape clears the selection and drops focus
  // off any focused card. Suppressed while the preview modal is open so it keeps
  // ownership of those keys.
  useSelectionKeyboard({
    enabled: status === "success",
    onSelectAll: selection.selectAll,
    onClear: range.clear,
  });

  const [phase, setPhase] = useState<"loading" | "fading" | "done">("loading");

  useEffect(() => {
    if (status === "loading") {
      setPhase("loading");
      return;
    }
    if (status !== "success") return;

    let timerId: number;
    const rafId = requestAnimationFrame(() => {
      setPhase("fading");
      timerId = window.setTimeout(() => setPhase("done"), 500);
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timerId);
    };
  }, [status]);

  if (status === "error") {
    // 404 = the share-link UID is invalid or the link has expired/been deleted.
    const isLinkUnavailable = error?.response?.status === 404;
    return (
      <ConditionalEnvironmentProvider
        condition={!!environmentRef.current}
        environment={environmentRef.current}
      >
        {isLinkUnavailable ? (
          <TransferPageSkeleton
            childrenVerticalAlignment="center"
            hasMailboxLoaded={!!environmentRef.current}
          >
            <ShareLinkErrorPage />
          </TransferPageSkeleton>
        ) : (
          <ErrorPage errorMessage={(error as Error)?.message ?? ""} />
        )}
      </ConditionalEnvironmentProvider>
    );
  }

  const totalSize = data?.files.reduce((acc, f) => acc + f.size, 0) ?? 0;
  const expiresAt = parseApiDate(data?.expiresAt);

  return (
    <ConditionalEnvironmentProvider
      condition={!!environmentRef.current}
      environment={environmentRef.current}
    >
      <TransferPageSkeleton
        childrenVerticalAlignment="top"
        hasMailboxLoaded={!!environmentRef.current}
      >
        <div
          ref={filesContainerRef}
          className="px-5 py-12 sm:px-[110px] sm:py-[70px]"
          // Clear-on-click + post-drag click suppression cover the whole
          // container (header, margins, files), so clicking any empty space
          // deselects. onClickCapture runs first to swallow the click that
          // follows a marquee drag before it can clear.
          onClickCapture={marquee.handleClickCapture}
          onClick={selection.handleBackgroundClick}
        >
          <div
            className="relative"
            style={{ minHeight: "calc(100vh - 200px)" }}
          >
            {status === "success" && (
              <div
                className="transition-opacity duration-500"
                style={{ opacity: phase === "loading" ? 0 : 1 }}
                onKeyDown={(e) =>
                  handleGridFocusCycle(e, {
                    containerRef: filesContainerRef,
                    boundarySelector: "[data-download-all]",
                  })
                }
              >
                <ShareLinkHeader
                  fileCount={files.length}
                  totalSize={totalSize}
                  expiresAt={expiresAt}
                />
                {files.length > 0 && (
                  <div className="sticky top-0 z-20 -mx-5 mt-6 flex justify-center bg-white/[0.7] px-5 py-4 backdrop-blur-[3px] sm:-mx-[110px] sm:px-[110px]">
                    <DownloadButton
                      shareLinkUid={shareLinkUid}
                      files={files}
                      selectedUids={selection.selectedUids}
                      totalSize={totalSize}
                    />
                  </div>
                )}
                {/* Marquee initiation + `select-none` are scoped to this
                    files-area wrapper, so a drag can't start from the header
                    (and header text stays selectable). Starting on a
                    `select-none` element also stops the browser from selecting
                    text as the box grows up over the header. Full-bleed
                    (negative margins cancel the page's side padding, then
                    re-pad the content) so a drag can also start from the
                    horizontal gutters beside the files. */}
                <div
                  className="-mx-5 select-none px-5 sm:-mx-[110px] sm:px-[110px]"
                  onMouseDown={marquee.handleMouseDown}
                >
                  <FilesSection
                    shareLinkUid={shareLinkUid}
                    files={files}
                    selectedUids={selection.selectedUids}
                    toggleSelect={range.toggle}
                    selectExclusive={range.selectExclusive}
                    selectRange={range.selectRange}
                    clearSelection={range.clear}
                    selectAll={selection.selectAll}
                    isCompact={isCompact}
                    sort={sort}
                    onSort={onSort}
                    isMarqueeActive={marquee.isDragging}
                  />
                </div>
              </div>
            )}
            {phase !== "done" && (
              <div
                className="bg-white transition-opacity duration-500"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: "auto",
                  minHeight: "100%",
                  opacity: phase === "fading" ? 0 : 1,
                  pointerEvents: phase !== "loading" ? "none" : "auto",
                  zIndex: 5,
                }}
              >
                <ShareLinkSkeletonContent />
              </div>
            )}
          </div>
          {marquee.isDragging && (
            <div
              ref={marquee.boxRef}
              className="pointer-events-none fixed left-0 top-0 z-30 rounded-[2px]"
              style={{
                border: "1px solid var(--primary-color)",
                backgroundColor:
                  "color-mix(in srgb, var(--primary-color) 18%, transparent)",
                willChange: "transform, width, height",
              }}
            />
          )}
        </div>
      </TransferPageSkeleton>
    </ConditionalEnvironmentProvider>
  );
}

// ==========================================================================
//                              Share link header
// ==========================================================================

type ShareLinkHeaderProp = {
  fileCount: number;
  totalSize: number;
  /** When the share-link expires; the "Gültig bis" segment is hidden if absent. */
  expiresAt?: Date;
};

function ShareLinkHeader({
  fileCount,
  totalSize,
  expiresAt,
}: ShareLinkHeaderProp) {
  const { t, i18n } = useTranslation();
  const humanSize = formatFileSize(totalSize, i18n.language);
  const primaryColor = cssVar("--primary-color");

  return (
    <div className="flex select-none flex-col items-center text-center">
      {/* Avatar: a primary-color disc with a white download arrow. */}
      <div
        className="flex h-[72px] w-[72px] items-center justify-center rounded-full"
        style={{ backgroundColor: primaryColor }}
      >
        <FontAwesomeIcon
          icon={faArrowDown as IconProp}
          style={{ color: "#FFFFFF", fontSize: 32 }}
        />
      </div>
      {/* avatar → title: 20px */}
      <div className="mt-5 text-[26px] font-normal leading-[34px] text-[#1A1A1A]">
        {t("Download the files provided")}
      </div>
      {/* title → description: 20px */}
      <div className="mt-5 max-w-[480px] text-[16px] font-normal leading-[20px] text-[#1A1A1A]">
        {t(
          "Here you'll find all the files provided for you. You can select individual files or download everything at once."
        )}
      </div>
      {/* description → info: 10px */}
      <div className="mt-2.5 flex items-center gap-3 text-[14px] font-normal leading-[18px] text-[#747474]">
        <span>
          {fileCount === 1
            ? t("1 file")
            : t("{{count}} files", { count: fileCount })}
        </span>
        <span className="text-[#D0D0D0]">|</span>
        <span>{t("{{size}} total size", { size: humanSize })}</span>
        {expiresAt && (
          <>
            <span className="text-[#D0D0D0]">|</span>
            <span>{`${t("Valid until")} ${formatDateDDMMYYYY(
              expiresAt
            )}`}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
//                              Files section
// ==========================================================================

/**
 * Builds the URL the preview overlay loads for a file: the inline download for
 * media / PDF / text (played/embedded/rendered directly from the raw bytes —
 * the drive preview endpoint only renders images, so text & CSV must be served
 * inline for the browser to display them), or a rendered preview image sized to
 * the on-screen frame (see {@link previewImageRequestSize}) for everything
 * else — instead of a fixed box, so we don't over-/under-fetch.
 */
function buildFilePreviewUrl(
  shareLinkUid: string,
  file: ShareLinkFile
): string {
  if (isInlinePreviewMime(file.mimeType)) {
    return getShareLinkFileDownloadUrl(shareLinkUid, file.uid, {
      inline: true,
    });
  }
  return getShareLinkFilePreviewUrl(
    shareLinkUid,
    file.uid,
    previewImageRequestSize()
  );
}

type FilesSectionProp = {
  shareLinkUid: string;
  /** The files in display order, as the page ordered them for this layout. */
  files: ShareLinkFile[];
  selectedUids: Set<string>;
  /** Cmd/Ctrl+click and the checkbox: add/remove a single file. */
  toggleSelect: (uid: string) => void;
  /** Plain click: the selection becomes exactly this file. */
  selectExclusive: (uid: string) => void;
  /** Shift+click: the selection becomes the range from the last click. */
  selectRange: (uid: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  /** True for the narrow (list) layout; the caller owns the media query. */
  isCompact: boolean;
  /** Active column sort (null until a header is clicked) + its click handler. */
  sort: FileSort | null;
  onSort: (key: FileSortKey) => void;
  /** True while a marquee drag is active (suppresses card hover + pointer cursor). */
  isMarqueeActive: boolean;
};

function FilesSection({
  shareLinkUid,
  files,
  selectedUids,
  toggleSelect,
  selectExclusive,
  selectRange,
  clearSelection,
  selectAll,
  isCompact,
  sort,
  onSort,
  isMarqueeActive,
}: FilesSectionProp) {
  const { t } = useTranslation();
  const hasHover = useHasHover();
  // Pointer devices follow the desktop file-manager convention (single click
  // selects, double click opens); touch keeps tap-to-open with long-press to
  // select, since it has no second gesture to spare.
  const activation: ItemActivation = hasHover ? "select" : "preview";
  // The checkbox is revealed by hovering the card/row wherever hovering exists,
  // narrow desktop windows included. Only a device with no hover at all (touch)
  // keeps it visible the whole time, since nothing else would ever surface it.
  const alwaysShowCheckbox = !hasHover;

  // The preview overlay is owned here — one shared instance for the whole
  // section — rather than per card/row. This keeps it open across a grid↔list
  // layout switch (which unmounts the originating item) and ensures only one
  // preview can be open at a time.
  const [previewFile, setPreviewFile] = useState<ShareLinkFile | null>(null);

  // Preview lightbox paging: previous/next walk the files in display order.
  const previewIndex = previewFile
    ? files.findIndex((f) => f.uid === previewFile.uid)
    : -1;
  const navigatePreview = useCallback(
    (direction: 1 | -1) => {
      setPreviewFile((current) => {
        if (!current) return current;
        const idx = files.findIndex((f) => f.uid === current.uid);
        return files[idx + direction] ?? current;
      });
    },
    [files]
  );

  // Paging can walk far from where the preview was opened, so closing it
  // scrolls the last-viewed file back into view. The scroll runs here rather
  // than inside the state updater, which has to stay a pure function of the
  // previous state (React is free to call it more than once).
  const closePreview = useCallback(() => {
    if (previewFile) {
      findSelectableItem(previewFile.uid)?.scrollIntoView({ block: "nearest" });
    }
    setPreviewFile(null);
  }, [previewFile]);

  // Force-refresh the guest cookie for the preview overlay: on a 401 the
  // preview retries after this resolves, and a failed refresh (rejection)
  // surfaces the error dialog. Forced (not throttled) since the cookie is known
  // to have expired; also awaited best-effort before the fallback download.
  const refreshAuth = useCallback(
    () => refreshGuestTokenNow(shareLinkUid),
    [shareLinkUid]
  );

  // Thumbnail URL builder injected into each grid card (it stays API-agnostic).
  // Uses the dedicated pre-rendered thumbnail endpoint. The card loads it with a
  // native `<img>`, and the endpoint sends `Cache-Control: no-cache`, so the
  // browser revalidates against the response ETag on every load: a changed file
  // is picked up without any cache-busting query param. (Backend follow-up to
  // content-key the URL by etag for long-lived caching: skp/drive-server#65.)
  const buildThumbnailUrl = useCallback(
    (file: ShareLinkFile) =>
      getShareLinkFileThumbnailUrl(shareLinkUid, file.uid),
    [shareLinkUid]
  );

  // Bundled thumbnail source handed to the grid cards. Memoized so cards keep
  // their memo across re-renders.
  const thumbnails = useMemo(
    () => ({ buildUrl: buildThumbnailUrl }),
    [buildThumbnailUrl]
  );

  if (files.length === 0) {
    return (
      <div className="pt-10 sm:pt-14">
        <p className="text-[14px] text-[#797979]">
          {t("This share-link contains no files.")}
        </p>
      </div>
    );
  }

  return (
    <div className="pt-10 sm:pt-14">
      <SelectionToolbar
        selectedCount={selectedUids.size}
        totalCount={files.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
      />
      {isCompact ? (
        // Rows bleed into the page gutter by their own inner padding, so the
        // rounded row fill gets the extra width while the row content stays on
        // the page's text grid.
        <div className="-mx-3">
          <FileList
            files={files}
            selectedUids={selectedUids}
            onToggleSelect={toggleSelect}
            onSelectExclusive={selectExclusive}
            onSelectRange={selectRange}
            onOpenPreview={setPreviewFile}
            sort={sort}
            onSort={onSort}
            activation={activation}
            checkboxAlwaysVisible={alwaysShowCheckbox}
            selecting={isMarqueeActive}
          />
        </div>
      ) : (
        <FileGrid
          files={files}
          selectedUids={selectedUids}
          onToggleSelect={toggleSelect}
          onSelectExclusive={selectExclusive}
          onSelectRange={selectRange}
          onOpenPreview={setPreviewFile}
          thumbnails={thumbnails}
          activation={activation}
          checkboxAlwaysVisible={alwaysShowCheckbox}
          selecting={isMarqueeActive}
        />
      )}
      {/* No `key`: paging swaps the per-file props on the same mounted overlay. */}
      {previewFile && (
        <PreviewPage
          transferRecipientId={shareLinkUid}
          objectUid={previewFile.uid}
          fileName={basenameFromPath(previewFile.path) || previewFile.uid}
          mimeType={previewFile.mimeType}
          aspectRatio={previewFile.aspectRatio}
          filePreviewUrl={
            new URL(buildFilePreviewUrl(shareLinkUid, previewFile))
          }
          fileDownloadUrl={getShareLinkFileDownloadUrl(
            shareLinkUid,
            previewFile.uid
          )}
          refreshAuth={refreshAuth}
          navigation={
            previewIndex >= 0 && files.length > 1
              ? {
                  hasPrevious: previewIndex > 0,
                  hasNext: previewIndex < files.length - 1,
                  onNavigate: navigatePreview,
                }
              : undefined
          }
          onClose={closePreview}
        />
      )}
    </div>
  );
}

// ==========================================================================
//                          Loading skeleton
// ==========================================================================

function Shimmer({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={`skeleton-shimmer ${className ?? ""}`} style={style} />
  );
}

function ShareLinkSkeletonContent() {
  const contentW = Math.max(0, window.innerWidth - 220);
  const cardGap = FILE_GRID_GAP_PX;
  const cardMinW = 174;
  const cols = Math.max(
    2,
    Math.floor((contentW + cardGap) / (cardMinW + cardGap))
  );
  const cardH = Math.ceil(cardMinW * (204 / 174)) + cardGap;
  const rows = Math.max(
    1,
    Math.ceil(Math.max(cardH, window.innerHeight - 500) / cardH) + 1
  );
  const cardCount = cols * rows;

  return (
    <div className="flex flex-col items-center text-center">
      <Shimmer className="rounded-full" style={{ width: 72, height: 72 }} />
      <Shimmer className="mt-6 rounded-md" style={{ width: 220, height: 28 }} />
      <Shimmer className="mt-3 rounded-md" style={{ width: 400, height: 18 }} />
      <Shimmer className="mt-2 rounded-md" style={{ width: 300, height: 18 }} />
      <Shimmer className="mt-4 rounded-md" style={{ width: 180, height: 18 }} />
      <Shimmer
        className="mt-10 rounded-full"
        style={{ width: 220, height: 42 }}
      />
      <div
        className="mt-10 grid w-full"
        style={{
          gridTemplateColumns: FILE_GRID_TEMPLATE_COLUMNS,
          gap: cardGap,
        }}
      >
        {Array.from({ length: cardCount }, (_, i) => (
          <div
            key={i}
            className="overflow-hidden rounded-[6px]"
            style={{ aspectRatio: "174 / 204" }}
          >
            <Shimmer className="h-full w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ==========================================================================
//                           Download button
// ==========================================================================

type DownloadButtonProp = {
  shareLinkUid: string;
  files: ShareLinkFile[];
  selectedUids: Set<string>;
  totalSize: number;
};

function DownloadButton({
  shareLinkUid,
  files,
  selectedUids,
  totalSize,
}: DownloadButtonProp) {
  const { t, i18n } = useTranslation();
  const buttonColor = cssVar("--primary-color");
  const selectedCount = selectedUids.size;
  const isSelectionActive = selectedCount > 0;
  const zipUrl = getShareLinkZipUrl(shareLinkUid);
  const humanSize = formatFileSize(totalSize, i18n.language);

  // Animate the selection button's width as its label grows/shrinks (e.g. the
  // selected count gaining a digit), so it eases between sizes instead of
  // snapping. `width: auto` can't be transitioned, so on each label change we
  // momentarily measure the natural width and then lock it to that pixel value
  // — the change from the previous locked width to the new one is what the CSS
  // transition animates. Measure/restore happen synchronously in a layout
  // effect, before paint, so there's no visible jump.
  const selectionButtonRef = useRef<HTMLButtonElement>(null);
  const [selectionButtonWidth, setSelectionButtonWidth] = useState<number>();
  useLayoutEffect(() => {
    if (!isSelectionActive) {
      setSelectionButtonWidth(undefined);
      return;
    }
    const el = selectionButtonRef.current;
    if (!el) return;
    const locked = el.style.width;
    el.style.width = "auto";
    const natural = el.offsetWidth;
    el.style.width = locked;
    setSelectionButtonWidth(natural);
  }, [isSelectionActive, selectedCount]);

  // Refresh the guest cookie before kicking off downloads — a download is the
  // one action we can't transparently retry, so it must start authenticated.
  const downloadSelected = useCallback(async () => {
    await ensureFreshGuestToken(shareLinkUid);
    files
      .filter((f) => selectedUids.has(f.uid))
      .forEach((file, idx) => {
        window.setTimeout(() => {
          const a = document.createElement("a");
          a.href = getShareLinkFileDownloadUrl(shareLinkUid, file.uid);
          a.download = basenameFromPath(file.path) || file.uid;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }, idx * 300);
      });
  }, [files, selectedUids, shareLinkUid]);

  // Same for the "download all" ZIP: keep the real <a href> (so right-click →
  // "save as" and middle-click still work), but on a normal click intercept it
  // to refresh the cookie first, then trigger the download programmatically.
  const downloadZip = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();
      await ensureFreshGuestToken(shareLinkUid);
      const a = document.createElement("a");
      a.href = zipUrl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    [shareLinkUid, zipUrl]
  );

  const buttonSx = {
    backgroundColor: buttonColor,
    color: "#FFFFFF",
    borderRadius: 18,
    textTransform: "none" as const,
    whiteSpace: "nowrap" as const,
    ":hover": { backgroundColor: buttonColor, filter: "brightness(0.95)" },
  };

  // The selected-count label rolls just the number (odometer-style). The count's
  // position varies by locale, so splice the RollingNumber into the translated
  // string where the interpolated count sits rather than concatenating.
  const selectionLabel =
    selectedCount === 1
      ? t("Download {{count}} selected file", { count: selectedCount })
      : t("Download {{count}} selected files", { count: selectedCount });
  let labelNode: ReactNode = t("Download all ({{size}})", { size: humanSize });
  if (isSelectionActive) {
    const token = String(selectedCount);
    const at = selectionLabel.indexOf(token);
    labelNode =
      at === -1 ? (
        selectionLabel
      ) : (
        <>
          {selectionLabel.slice(0, at)}
          <RollingNumber value={selectedCount} />
          {selectionLabel.slice(at + token.length)}
        </>
      );
  }

  const buttonContent = (
    <>
      <DownloadAllButtonIcon color="#FFFFFF" height={18.767} width={15.796} />
      {/* Text spec (downloadAllbuttonTextSpec): IBM Plex Sans Medium, 16px,
          line-height 20, letter-spacing 0.16px, white. */}
      <span className="ml-2 font-ibm-plex-sans text-[16px] font-medium leading-[20px] tracking-[0.16px]">
        {labelNode}
      </span>
    </>
  );

  return (
    <div className="flex justify-center">
      {isSelectionActive ? (
        <Button
          ref={selectionButtonRef}
          type="button"
          variant="contained"
          onClick={downloadSelected}
          sx={buttonSx}
          style={{
            width: selectionButtonWidth,
            transition: "width 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
          draggable={false}
          data-download-all
        >
          {buttonContent}
        </Button>
      ) : (
        <a
          href={zipUrl}
          download=""
          onClick={downloadZip}
          aria-label={t("Download all as ZIP")}
          draggable={false}
          data-download-all
        >
          <Button type="button" variant="contained" sx={buttonSx} tabIndex={-1}>
            {buttonContent}
          </Button>
        </a>
      )}
    </div>
  );
}
